/**
 * Client HTTP de l'API.
 *
 * Deux responsabilités qui n'ont pas leur place dans les composants : porter le jeton d'accès
 * et le renouveler silencieusement quand il expire.
 *
 * Le jeton d'accès est gardé **en mémoire**, jamais dans `localStorage` : un XSS y accéderait
 * directement. Le jeton de rafraîchissement, lui, vit dans un cookie `HttpOnly` hors de portée
 * de JavaScript (PRD NFR-2).
 */

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

let accessToken: string | null = null;
let refreshPromise: Promise<string | null> | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

/** Organisation courante, envoyée en en-tête pour résoudre le contexte côté API. */
let organizationId: string | null = null;

export function setOrganizationId(id: string | null): void {
  organizationId = id;
}

interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  /** Interne : empêche une boucle de rafraîchissement infinie. */
  _retried?: boolean;
}

/**
 * Cœur du client : porte le jeton d'accès et l'en-tête d'organisation, et renouvelle une fois
 * sur `401`. Renvoie la `Response` brute — c'est ce que partagent `apiFetch` (qui en tire du JSON)
 * et `apiDownload` (qui en tire un fichier binaire).
 */
async function requestWithAuth(path: string, options: RequestOptions): Promise<Response> {
  const { body, _retried, headers, ...rest } = options;

  const response = await fetch(`/api/v1${path}`, {
    ...rest,
    // Indispensable : sans cela le cookie de rafraîchissement n'est pas envoyé.
    credentials: 'include',
    headers: {
      ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      ...(organizationId ? { 'X-Org-Id': organizationId } : {}),
      ...headers,
    },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });

  // Une seule tentative de renouvellement : si le refresh échoue aussi, la session est morte.
  if (response.status === 401 && !_retried) {
    const renewed = await refreshAccessToken();
    if (renewed) {
      return requestWithAuth(path, { ...options, _retried: true });
    }
  }

  return response;
}

export async function apiFetch<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const response = await requestWithAuth(path, options);

  if (!response.ok) {
    throw new ApiError(response.status, await extractErrorMessage(response));
  }

  // Corps vide (204, ou 200 sans contenu comme un DELETE) : ne pas tenter de parser du JSON.
  // Un `response.json()` sur un corps vide lève, ce qui faisait passer une suppression réussie
  // pour un échec côté client.
  if (response.status === 204) {
    return undefined as T;
  }
  const text = await response.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

/**
 * Ouvre un flux (SSE) authentifié et renvoie la `Response` brute à lire en streaming.
 *
 * On passe par `fetch` — et non `EventSource` — précisément pour **porter le jeton d'accès** en
 * en-tête `Authorization` : `EventSource` ne sait pas poser d'en-tête, ce qui obligerait à faire
 * transiter le jeton en clair dans l'URL. Le renouvellement sur `401` est hérité de `requestWithAuth`.
 * L'appelant lit `response.body` et annule via le `signal`.
 */
export async function apiStream(path: string, signal: AbortSignal): Promise<Response> {
  return requestWithAuth(path, { method: 'GET', headers: { Accept: 'text/event-stream' }, signal });
}

/**
 * Télécharge un fichier servi par l'API (export d'annotations, etc.).
 *
 * On ne peut pas se contenter d'un `<a href>` : la route est protégée par le jeton d'accès, porté
 * en en-tête `Authorization` et non par le cookie. On récupère donc le corps authentifié en blob,
 * puis on déclenche l'enregistrement via une URL d'objet éphémère.
 */
export async function apiDownload(path: string, fallbackName: string): Promise<void> {
  const response = await requestWithAuth(path, { method: 'GET' });
  if (!response.ok) {
    throw new ApiError(response.status, await extractErrorMessage(response));
  }
  const blob = await response.blob();
  const filename =
    filenameFromDisposition(response.headers.get('content-disposition')) ?? fallbackName;

  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

/** Extrait le nom de fichier d'un en-tête `Content-Disposition`, ou `null` s'il est absent. */
function filenameFromDisposition(value: string | null): string | null {
  if (!value) return null;
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(value);
  return match?.[1] ? decodeURIComponent(match[1]) : null;
}

/**
 * Renouvelle le jeton d'accès.
 *
 * La promesse est mémorisée le temps de l'appel : quand cinq requêtes reçoivent `401`
 * simultanément, elles doivent partager un seul rafraîchissement. En déclencher cinq ferait
 * tourner le jeton cinq fois, et la détection de rejeu côté serveur révoquerait la session —
 * l'utilisateur serait déconnecté par le mécanisme censé le protéger.
 */
async function refreshAccessToken(): Promise<string | null> {
  refreshPromise ??= (async () => {
    try {
      const response = await fetch('/api/v1/auth/refresh', {
        method: 'POST',
        credentials: 'include',
      });
      if (!response.ok) {
        setAccessToken(null);
        return null;
      }
      const data = (await response.json()) as { accessToken: string };
      setAccessToken(data.accessToken);
      return data.accessToken;
    } catch {
      setAccessToken(null);
      return null;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

async function extractErrorMessage(response: Response): Promise<string> {
  try {
    const data = (await response.json()) as { message?: string | string[] };
    if (Array.isArray(data.message)) return data.message.join(', ');
    if (data.message) return data.message;
  } catch {
    // Corps non JSON : on retombe sur le texte de statut.
  }
  return response.statusText || `Erreur HTTP ${response.status}`;
}
